import { useEffect, useState } from "react";
import { get, ago } from "../lib/api";
import { Card, Button, Carregando } from "../lib/ui";
import {
  CircleCheck,
  TriangleAlert,
  CircleAlert,
  Server,
  Smartphone,
  ListChecks,
  HardDriveDownload,
  HardDrive,
  CalendarClock,
  Flame,
  MessageSquare,
  RefreshCw,
} from "lucide-react";

/**
 * O que está de pé, e o que não está.
 *
 * Existe por causa de três falhas que ficaram semanas no ar sem ninguém
 * perceber: o simulador quebrado desde que nasceu, o "Assumir" que não fazia
 * nada e o custo do simulador que sumia. Nenhuma dava erro visível — e não
 * havia lugar nenhum que respondesse "está tudo funcionando?".
 *
 * A tela não substitui o aviso no WhatsApp: o que faz lead ficar sem resposta
 * chega no celular de quem estiver marcado em Avisos. Aqui é onde se olha
 * quando a dúvida bateu.
 */

interface Problema {
  chave: string;
  nivel: "erro" | "aviso";
  texto: string;
}

interface SaudeDados {
  servicos: { servico: string; em: string; segundos: number }[];
  canais: { label: string; status: string; active: boolean; ultimo_visto: string | null }[];
  turnos_erro: number;
  ultimo_erro: { quando: string; texto: string } | null;
  descartes: number;
  lacunas: number;
  turnos_presos: number;
  google_conectado: boolean;
  aquecimento_em: string | null;
  ultima_mensagem_lead: string | null;
  ultima_resposta_ia: string | null;
  followups_falhos: number;
  enviadas: number;
  sem_entrega: number;
  conversas_sem_entrega: number;
  recibos_desde: string | null;
  google_leitura_erro: string | null;
  google_leitura_falhou_em: string | null;
  anexos_bytes: number;
  anexos_arquivos: number;
  midia_bytes: number;
  midia_arquivos: number;
  livre_bytes: number | null;
  backup_em: string | null;
  filas: Record<string, number>;
  problemas: Problema[];
}

const ROTULO_SERVICO: Record<string, string> = {
  worker: "Worker (responde os leads)",
  gateway: "Gateway (fala com o WhatsApp)",
  api: "Painel",
};

export function Saude() {
  const [d, setD] = useState<SaudeDados | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = () =>
    get<SaudeDados>("/saude")
      .then((r) => {
        setD(r);
        setErro(null);
      })
      .catch((e: Error) => setErro(e.message));

  useEffect(() => {
    carregar();
    // Meio minuto: a tela fica aberta numa aba enquanto se trabalha, e um
    // número velho aqui vale menos que nenhum.
    const t = setInterval(carregar, 30_000);
    return () => clearInterval(t);
  }, []);

  if (erro) return <Card className="p-5 text-sm text-danger">{erro}</Card>;
  if (!d) return <Carregando />;

  const erros = d.problemas.filter((p) => p.nivel === "erro");
  const avisos = d.problemas.filter((p) => p.nivel === "aviso");

  return (
    <div className="space-y-5">
      {/* O veredito, em uma linha. */}
      <Card
        className="p-5 flex items-start gap-4"
        stripe={erros.length > 0 ? "danger" : avisos.length > 0 ? "warm" : "ok"}
      >
        <span
          className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
            erros.length > 0
              ? "bg-danger-dim text-danger"
              : avisos.length > 0
                ? "bg-warm-dim text-warm"
                : "bg-ok-dim text-ok"
          }`}
        >
          {erros.length > 0 ? (
            <CircleAlert size={20} />
          ) : avisos.length > 0 ? (
            <TriangleAlert size={20} />
          ) : (
            <CircleCheck size={20} />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-ink">
            {erros.length > 0
              ? "Tem coisa quebrada agora"
              : avisos.length > 0
                ? "Funcionando, com pontos para olhar"
                : "Tudo funcionando"}
          </h2>
          {d.problemas.length === 0 ? (
            <p className="text-sm text-ink-3 mt-1">
              Processos respondendo, WhatsApp conectado, nenhuma resposta com
              erro nas últimas 24 horas.
            </p>
          ) : (
            <ul className="mt-2.5 space-y-1.5">
              {d.problemas.map((p) => (
                <li key={p.chave} className="flex gap-2 text-sm">
                  <span
                    className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                      p.nivel === "erro" ? "bg-danger" : "bg-warm"
                    }`}
                  />
                  <span className={p.nivel === "erro" ? "text-ink" : "text-ink-2"}>
                    {p.texto}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button variant="ghost" icone={RefreshCw} onClick={carregar} title="Atualizar" />
      </Card>

      <div className="grid md:grid-cols-2 gap-3">
        <Bloco titulo="Processos" icone={Server}>
          {["worker", "gateway", "api"].map((nome) => {
            const b = d.servicos.find((x) => x.servico === nome);
            const vivo = b !== undefined && b.segundos <= 300;
            return (
              <Linha
                key={nome}
                rotulo={ROTULO_SERVICO[nome] ?? nome}
                ok={vivo}
                valor={
                  b
                    ? vivo
                      ? "respondendo"
                      : `parado há ${Math.round(b.segundos / 60)} min`
                    : "sem sinal"
                }
              />
            );
          })}
        </Bloco>

        <Bloco titulo="WhatsApp" icone={Smartphone}>
          {d.canais.length === 0 && (
            <p className="text-xs text-ink-3">Nenhum número cadastrado.</p>
          )}
          {d.canais.map((c) => (
            <Linha
              key={c.label}
              rotulo={c.label}
              ok={c.status === "connected"}
              valor={
                c.status === "connected"
                  ? "conectado"
                  : !c.active
                    ? "desativado"
                    : c.status
              }
            />
          ))}
          {/* Entrega: mensagem que sai e nunca chega é o sinal de bloqueio. */}
          <Linha
            rotulo="Entregues (24h)"
            ok={d.enviadas === 0 || d.sem_entrega < d.enviadas}
            valor={
              d.recibos_desde === null
                ? "sem recibos ainda"
                : d.enviadas === 0
                  ? "nada enviado"
                  : `${d.enviadas - d.sem_entrega} de ${d.enviadas}`
            }
          />
          {d.recibos_desde === null && (
            <p className="text-[11px] text-ink-3 mt-1 leading-relaxed">
              Nenhum recibo chegou ainda. Mensagem enviada antes desta versão
              nunca terá entrega — ninguém estava ouvindo. A conta começa no
              primeiro recibo.
            </p>
          )}
          {d.sem_entrega > 0 && (
            <p className="text-[11px] text-ink-3 mt-1 leading-relaxed">
              {d.sem_entrega} sem entrega em {d.conversas_sem_entrega} conversa(s).
              Uma é celular desligado; várias ao mesmo tempo costuma ser bloqueio.
            </p>
          )}
        </Bloco>

        <Bloco titulo="Filas" icone={ListChecks}>
          {Object.keys(d.filas).length === 0 ? (
            <p className="text-xs text-ink-3">
              Sem resposta do Redis — se os processos estão de pé, é só a
              leitura das filas que falhou.
            </p>
          ) : (
            Object.entries(d.filas).map(([fila, n]) => (
              <Linha
                key={fila}
                rotulo={fila}
                ok={n === 0}
                valor={n === 0 ? "sem falhas" : `${n} job(s) falharam`}
              />
            ))
          )}
        </Bloco>

        <Bloco titulo="Respostas da IA" icone={MessageSquare}>
          <Linha
            rotulo="Falharam (24h)"
            ok={d.turnos_erro === 0}
            valor={String(d.turnos_erro)}
          />
          <Linha
            rotulo="Travadas agora"
            ok={d.turnos_presos === 0}
            valor={String(d.turnos_presos)}
          />
          <Linha
            rotulo="Descartadas por vazamento (24h)"
            ok={d.descartes === 0}
            valor={String(d.descartes)}
          />
          <Linha
            rotulo="Follow-ups que não saíram (24h)"
            ok={d.followups_falhos === 0}
            valor={String(d.followups_falhos)}
          />
          {d.ultimo_erro && (
            <p className="text-[11px] text-ink-3 mt-2 leading-relaxed">
              Último erro, {ago(d.ultimo_erro.quando)}: {d.ultimo_erro.texto}
            </p>
          )}
        </Bloco>

        <Bloco titulo="Rotinas" icone={CalendarClock}>
          <Linha
            rotulo="Backup do banco"
            ok={
              d.backup_em !== null &&
              Date.now() - new Date(d.backup_em).getTime() < 36 * 3600_000
            }
            valor={d.backup_em ? ago(d.backup_em) : "nenhum ainda"}
          />
          <Linha
            rotulo="Aquecimento do cache"
            ok={
              d.aquecimento_em !== null &&
              Date.now() - new Date(d.aquecimento_em).getTime() < 3 * 3600_000
            }
            valor={d.aquecimento_em ? ago(d.aquecimento_em) : "nunca"}
          />
          <Linha
            rotulo="Agenda do Google"
            ok={d.google_conectado && !d.google_leitura_erro}
            valor={
              !d.google_conectado
                ? "não conectada"
                : d.google_leitura_erro
                  ? `leitura falhando${d.google_leitura_falhou_em ? ` desde ${ago(d.google_leitura_falhou_em)}` : ""}`
                  : "conectada"
            }
          />
          <Linha
            rotulo="Perguntas sem resposta"
            ok={d.lacunas < 5}
            valor={String(d.lacunas)}
          />
        </Bloco>

        <Bloco titulo="Espaço em disco" icone={HardDrive}>
          <Linha
            rotulo="Mídia das conversas"
            ok={true}
            valor={`${tamanho(d.midia_bytes)} · ${d.midia_arquivos} arquivo(s)`}
          />
          <Linha
            rotulo="Fotos e documentos do painel"
            ok={true}
            valor={`${tamanho(d.anexos_bytes)} · ${d.anexos_arquivos} arquivo(s)`}
          />
          <Linha
            rotulo="Livre no disco"
            ok={d.livre_bytes === null || d.livre_bytes > 2 * 1024 ** 3}
            valor={d.livre_bytes === null ? "não deu para ler" : tamanho(d.livre_bytes)}
          />
          <p className="text-[11px] text-ink-3 mt-2 leading-relaxed">
            A mídia que os leads mandam só cresce — ninguém apaga. Com o disco
            cheio, conversa, backup e arquivo param de ser gravados ao mesmo
            tempo, por isso o aviso sai com 2 GB livres.
          </p>
        </Bloco>

        <Bloco titulo="Fluxo" icone={Flame}>
          <Linha
            rotulo="Última mensagem de lead"
            ok={d.ultima_mensagem_lead !== null}
            valor={d.ultima_mensagem_lead ? ago(d.ultima_mensagem_lead) : "nenhuma"}
          />
          <Linha
            rotulo="Última resposta da IA"
            ok={d.ultima_resposta_ia !== null}
            valor={d.ultima_resposta_ia ? ago(d.ultima_resposta_ia) : "nenhuma"}
          />
          <p className="text-[11px] text-ink-3 mt-2 leading-relaxed">
            Lead falando e IA calada por muito tempo é sinal de fila parada,
            mesmo com tudo verde aqui em cima.
          </p>
        </Bloco>
      </div>

      <p className="text-xs text-ink-3 max-w-prose leading-relaxed">
        <HardDriveDownload size={12} className="inline mb-0.5 mr-1" />
        Quem estiver marcado em <strong className="text-ink-2">Avisos</strong> com
        "Sistema com problema" recebe no WhatsApp quando algo quebra de verdade.
        O aviso sai uma vez por problema e não repete enquanto for o mesmo.
      </p>
    </div>
  );
}

function Bloco({
  titulo,
  icone: Icone,
  children,
}: {
  titulo: string;
  icone: typeof Server;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-ink mb-3">
        <Icone size={15} className="text-accent" />
        {titulo}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </Card>
  );
}

function Linha({
  rotulo,
  ok,
  valor,
}: {
  rotulo: string;
  ok: boolean;
  valor: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-ink-2 flex items-center gap-2 min-w-0">
        <span
          className={`w-1.5 h-1.5 rounded-full shrink-0 ${ok ? "bg-ok" : "bg-danger"}`}
        />
        <span className="truncate">{rotulo}</span>
      </span>
      <span className={`shrink-0 tabular-nums ${ok ? "text-ink-3" : "text-danger"}`}>
        {valor}
      </span>
    </div>
  );
}

/** 2,4 GB / 312 MB / 48 kB -- a unidade que cabe no numero. */
function tamanho(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${bytes} B`;
}
